/* @flow */
import { Locale, User, Auth } from 'services'
import Vue from 'vue'
import VueI18n from 'vue-i18n'
Vue.use(VueI18n)

let areLocalesBeingAdded = false
/**
 * Sets up i18n, setting languages from cache and getting new ones from API.
 */

import locales from './locales/locales'

const localesByKey = locales.reduce((acc, l) => {
  return {
    ...acc,
    [l.item.name]: l.item.descriptive
  }
}, {})

export const i18n = new VueI18n({
  locale: 'en',
  fallbackLocale: 'en',
  messages: localesByKey
})

export function getUserPreferenceLang() {
  const user = Auth.getLoggedInUser()
  if (!user) return Promise.reject(Error('No user to get preferences from'))
  return User.byName(user).then(([user]) => {
    return user && user.preferences ? user.preferences.language || 'en' : 'en'
  })
}

export function SetupLocales() {
  let availableLanguages = []
  const cachedLanguages = Locale.getCachedLocales()
  let currentLang = Locale.getCachedLangKey()

  /**
   * Set a new language and update cached language key (currentLang).
   */
  const setLang = (lang: string): any => {
    i18n.locale = ''
    i18n.locale = lang
    currentLang = lang
    Locale.updateCachedLang(lang)
  }

  /**
   * Expose language data / switching methods to Vue components.
   */
  Vue.prototype.$locale = {
    change: (lang: string): any => setLang(lang),
    current: (): string => i18n.locale,
    availableLanguages: availableLanguages
  }

  /**
   * Load all language locales (also updates availableLanguages).
   */
  const loadLangLocales = (): any => {
    return Locale.getLocales()
      .then(({ localeKeys, locales }: { localeKeys: Array<string>, locales: Array<any> }) => {
        availableLanguages = localeKeys
        locales.forEach(l => i18n.setLocaleMessage(l.name, l.descriptive))
        Vue.prototype.$locale.availableLanguages = availableLanguages
        if (!currentLang) setLang(availableLanguages[0])

        if ((!locales || !locales.length) && !areLocalesBeingAdded) {
          return addLocales()
        }
      })
      .catch(err => console.error(err, 'Error loading locales'))
  }

  const addLocales = () => {
    areLocalesBeingAdded = true
    return Locale.addLocales()
      .then(() => {
        areLocalesBeingAdded = false
      })
      .catch(() => {
        areLocalesBeingAdded = false
      })
  }

  getUserPreferenceLang()
    .then(setLang)
    .catch(e => console.debug(e))

  /**
   * i18n worklow.
   *
   * 1. We loop through cached locales and set them in the i18n module.
   * 2. We look if a language key (currentLang) is saved in cache.
   *   2.1. If there is, we set that language.
   * 3. We request all locales and set their keys in the i18n module.
   *   3.1. If there's no set language key (currentLang) by this point, we set the first loaded one and save that setting to cache.
   */

  Object.keys(cachedLanguages).forEach(key => i18n.setLocaleMessage(key, cachedLanguages[key])) // 1.
  if (currentLang) setLang(currentLang) // 2.
  loadLangLocales() // 3.

  return i18n
}
